/**
 * MiningTransports - Animated cargo ships transporting materials
 * Routes: EAHP Construction Base → Mercury Spaceport (dark side)
 * Ships approach Mercury from the lit side, orbit around to land on dark side
 * Visual: Industrial freighters with cargo containers and engine glow
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

// === PRE-ALLOCATED VECTORS FOR PERFORMANCE ===
// Avoids creating new objects every frame (GC optimization)
const _currentPos = new THREE.Vector3();
const _nextPos = new THREE.Vector3();
const _direction = new THREE.Vector3();
const _defaultForward = new THREE.Vector3(0, 0, 1);
const _targetQuat = new THREE.Quaternion();

interface MiningTransportsProps {
  mercuryPosition: [number, number, number];
  sunPosition: [number, number, number];
  eahpRadius: number;
  shipCount?: number;
}

interface ShipData {
  id: number;
  progress: number;
  speed: number;
  pathOffset: THREE.Vector3;
  returning: boolean;
  scale: number;
  orbitPhase: number;  // For orbital approach variation
}

export function MiningTransports({
  mercuryPosition,
  sunPosition,
  eahpRadius,
  shipCount = 12
}: MiningTransportsProps) {
  const shipsRef = useRef<ShipData[]>([]);
  const { isClipMode } = useStore();

  // Initialize ship data - LANE-BASED SYSTEM (no collisions by design)
  useMemo(() => {
    shipsRef.current = Array.from({ length: shipCount }, (_, i) => {
      // Each ship gets a unique lane based on ID
      const laneIndex = i % 4; // 4 lanes
      const waveIndex = Math.floor(i / 4); // Which wave this ship is in

      // Deterministic progress offset - ships evenly spaced
      const progressOffset = (i / shipCount);

      // Lane offsets - each lane is separated horizontally and vertically
      const laneOffsets = [
        new THREE.Vector3(-1.5, 0.5, -1),
        new THREE.Vector3(1.5, -0.3, 1),
        new THREE.Vector3(-0.8, 0.8, 1.2),
        new THREE.Vector3(0.8, -0.5, -1.2),
      ];

      return {
        id: i,
        progress: progressOffset, // Evenly distributed
        speed: 0.012 + (laneIndex * 0.002), // Slightly different speeds per lane
        pathOffset: laneOffsets[laneIndex],
        returning: i % 2 === 0, // Alternate directions
        scale: 0.12 + (laneIndex * 0.02), // Consistent size per lane
        orbitPhase: (i / shipCount) * Math.PI * 2
      };
    });
  }, [shipCount]);

  // Define route endpoints
  const sunPoint = useMemo(() => new THREE.Vector3(...sunPosition), [sunPosition]);
  const mercuryPoint = useMemo(() => new THREE.Vector3(...mercuryPosition), [mercuryPosition]);

  // EAHP construction base - positioned on the swarm ring
  const eahpBasePoint = useMemo(() => {
    return new THREE.Vector3(
      sunPosition[0] + eahpRadius * 0.7,
      1,
      eahpRadius * 0.7
    );
  }, [sunPosition, eahpRadius]);

  // Mercury spaceport - on DARK side (opposite from Sun)
  const mercurySpaceportPoint = useMemo(() => {
    // Direction from sun to mercury
    const sunToMercury = mercuryPoint.clone().sub(sunPoint).normalize();
    // Spaceport is on the far side of Mercury from the Sun
    return mercuryPoint.clone().add(sunToMercury.clone().multiplyScalar(2.5));
  }, [mercuryPoint, sunPoint]);

  // Show mining transports during clip - they're part of the story!
  // (removed isClipMode check - we want to see the active mining operations)

  return (
    <group>
      {shipsRef.current.map((ship) => (
        <MiningShip
          key={ship.id}
          shipData={ship}
          eahpPoint={eahpBasePoint}
          mercuryCenter={mercuryPoint}
          spaceportPoint={mercurySpaceportPoint}
          sunPosition={sunPoint}
        />
      ))}
    </group>
  );
}

function MiningShip({
  shipData,
  eahpPoint,
  mercuryCenter,
  spaceportPoint,
  sunPosition
}: {
  shipData: ShipData;
  eahpPoint: THREE.Vector3;
  mercuryCenter: THREE.Vector3;
  spaceportPoint: THREE.Vector3;
  sunPosition: THREE.Vector3;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const progressRef = useRef(shipData.progress);
  const engineGlowRef = useRef<THREE.PointLight>(null);

  // Calculate orbital path around Mercury
  // Ships approach from Sun side, orbit around to dark side spaceport
  // CRITICAL: Keep ships OUTSIDE Mercury's radius at all times
  const pathPoints = useMemo(() => {
    // Mercury radius is 1.8, need to stay well outside
    const mercuryRadius = 1.8;
    const safeOrbitRadius = mercuryRadius + 2.5; // 4.3 units from center

    // Direction from sun to mercury (used for orbital geometry)
    const sunToMercury = mercuryCenter.clone().sub(sunPosition).normalize();

    // Perpendicular vector for orbital plane (horizontal orbit around Mercury)
    const orbitAxis = new THREE.Vector3(0, 1, 0);
    const perpendicular = sunToMercury.clone().cross(orbitAxis).normalize();

    // Determine which side the ship orbits (clockwise or counter-clockwise)
    const orbitDirection = shipData.orbitPhase > Math.PI ? 1 : -1;

    // Point 1: EAHP departure
    const p0 = eahpPoint.clone().add(shipData.pathOffset.clone().multiplyScalar(0.3));

    // Point 2: Midway approach (elevated arc) - well away from Mercury
    const midDirection = eahpPoint.clone().sub(mercuryCenter).normalize();
    const p1 = mercuryCenter.clone()
      .add(midDirection.clone().multiplyScalar(12))
      .add(new THREE.Vector3(0, 2 + shipData.pathOffset.y, 0));

    // Point 3: Mercury approach - Sun-facing side, OUTSIDE the planet
    // Start the orbital approach from the sun-facing side at safe distance
    const p2 = mercuryCenter.clone()
      .add(sunToMercury.clone().negate().multiplyScalar(safeOrbitRadius))
      .add(perpendicular.clone().multiplyScalar(orbitDirection * safeOrbitRadius * 0.3))
      .add(new THREE.Vector3(0, 0.8, 0));

    // Point 4: Side of Mercury - perpendicular to sun direction
    // Ships swing around the side of Mercury at safe distance
    const p3 = mercuryCenter.clone()
      .add(perpendicular.clone().multiplyScalar(orbitDirection * safeOrbitRadius))
      .add(new THREE.Vector3(0, 0.5, 0));

    // Point 5: Approaching dark side - between side and spaceport
    const p4 = mercuryCenter.clone()
      .add(sunToMercury.clone().multiplyScalar(safeOrbitRadius * 0.8))
      .add(perpendicular.clone().multiplyScalar(orbitDirection * safeOrbitRadius * 0.5))
      .add(new THREE.Vector3(0, 0.3, 0));

    // Point 6: Final approach to spaceport (dark side) - land ON the surface
    const p5 = spaceportPoint.clone()
      .add(new THREE.Vector3(0, 0.15, 0))  // Just slightly above surface for landing
      .add(shipData.pathOffset.clone().multiplyScalar(0.1));

    return [p0, p1, p2, p3, p4, p5];
  }, [eahpPoint, mercuryCenter, spaceportPoint, sunPosition, shipData.pathOffset, shipData.orbitPhase]);

  // Catmull-Rom spline interpolation for smooth path
  // PERFORMANCE: Takes output parameter to avoid allocations
  const getPointOnPath = (t: number, out: THREE.Vector3): void => {
    const points = pathPoints;
    const numSegments = points.length - 1;
    const segment = Math.min(Math.floor(t * numSegments), numSegments - 1);
    const localT = (t * numSegments) - segment;

    // Get 4 points for Catmull-Rom (with clamping at edges)
    const p0 = points[Math.max(0, segment - 1)];
    const p1 = points[segment];
    const p2 = points[Math.min(points.length - 1, segment + 1)];
    const p3 = points[Math.min(points.length - 1, segment + 2)];

    // Catmull-Rom interpolation
    const t2 = localT * localT;
    const t3 = t2 * localT;

    out.set(
      0.5 * ((2 * p1.x) + (-p0.x + p2.x) * localT + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
      0.5 * ((2 * p1.y) + (-p0.y + p2.y) * localT + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
      0.5 * ((2 * p1.z) + (-p0.z + p2.z) * localT + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3)
    );
  };

  useFrame((state, delta) => {
    if (!groupRef.current) return;

    // Update progress
    progressRef.current += delta * shipData.speed;
    if (progressRef.current > 1) {
      progressRef.current = 0;
      shipData.returning = !shipData.returning;
    }

    // Get current position on path
    // Returning ships go from spaceport back to EAHP (reverse direction)
    const t = shipData.returning ? 1 - progressRef.current : progressRef.current;

    // PERFORMANCE: Use pre-allocated vectors instead of creating new ones
    getPointOnPath(t, _currentPos);
    getPointOnPath(Math.min(t + 0.01, 1), _nextPos);

    // Position ship
    groupRef.current.position.copy(_currentPos);

    // Orient ship towards direction of travel
    // Ship model: Z+ = nose (forward), Z- = engines (back)
    // PERFORMANCE: Use pre-allocated _direction vector
    _direction.subVectors(_nextPos, _currentPos);
    if (_direction.length() > 0.001) {
      _direction.normalize();

      // Simple quaternion: rotate from default Z-forward to target direction
      // PERFORMANCE: Use pre-allocated _targetQuat and _defaultForward
      _targetQuat.setFromUnitVectors(_defaultForward, _direction);

      // Smooth interpolation
      groupRef.current.quaternion.slerp(_targetQuat, 0.1);
    }

    // Engine glow intensity - stronger when in transit, dimmer near landing
    const nearLanding = t > 0.85 || t < 0.15;
    const baseIntensity = nearLanding ? 0.5 : 1.2;
    if (engineGlowRef.current) {
      engineGlowRef.current.intensity = baseIntensity + Math.sin(state.clock.elapsedTime * 10 + shipData.id) * 0.3;
    }
  });

  return (
    <group ref={groupRef} scale={shipData.scale}>
      {/* === SPACEX STARSHIP CARGO VARIANT === */}

      {/* Main cylindrical hull - SpaceX stainless steel look */}
      <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.25, 0.25, 1.8, 16]} />
        <meshStandardMaterial
          color={0xf5f5f5}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>

      {/* Nose cone */}
      <mesh position={[0, 0, 0.95]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.25, 0.5, 16]} />
        <meshStandardMaterial
          color={0xeeeeee}
          metalness={0.6}
          roughness={0.35}
        />
      </mesh>

      {/* Black heat shield tiles on nose */}
      <mesh position={[0, -0.12, 0.7]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.3, 0.02, 0.4]} />
        <meshStandardMaterial color={0x1a1a1a} roughness={0.8} metalness={0.2} />
      </mesh>

      {/* Forward flaps (canards) */}
      {[0, Math.PI].map((angle, i) => (
        <mesh key={`fwd-${i}`} position={[Math.cos(angle) * 0.28, 0, 0.6]} rotation={[0.2, angle, 0]}>
          <boxGeometry args={[0.02, 0.25, 0.2]} />
          <meshStandardMaterial color={0x1a1a1a} metalness={0.5} roughness={0.4} />
        </mesh>
      ))}

      {/* Aft flaps */}
      {[0, Math.PI].map((angle, i) => (
        <mesh key={`aft-${i}`} position={[Math.cos(angle) * 0.28, 0, -0.5]} rotation={[0.15, angle, 0]}>
          <boxGeometry args={[0.02, 0.35, 0.25]} />
          <meshStandardMaterial color={0x1a1a1a} metalness={0.5} roughness={0.4} />
        </mesh>
      ))}

      {/* Black accent band - SpaceX style */}
      <mesh position={[0, 0, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.26, 0.26, 0.08, 16]} />
        <meshStandardMaterial color={0x1a1a1a} roughness={0.5} metalness={0.3} />
      </mesh>

      {/* Cargo bay indicators (when loaded) */}
      {!shipData.returning && (
        <>
          <mesh position={[0, 0, -0.2]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.27, 0.27, 0.5, 16]} />
            <meshStandardMaterial
              color={0x886633}
              metalness={0.5}
              roughness={0.6}
            />
          </mesh>
        </>
      )}

      {/* === RAPTOR ENGINES (SpaceX style) === */}
      {/* Engine skirt */}
      <mesh position={[0, 0, -0.95]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.22, 0.28, 0.15, 16]} />
        <meshStandardMaterial color={0x333333} metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Raptor engine bells - 3 engines like Starship */}
      {[0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].map((angle, i) => (
        <group key={i} position={[Math.cos(angle) * 0.12, Math.sin(angle) * 0.12, -1]}>
          {/* Engine bell */}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.08, 0.15, 12, 1, true]} />
            <meshStandardMaterial
              color={0x444444}
              metalness={0.9}
              roughness={0.2}
              side={THREE.DoubleSide}
            />
          </mesh>
          {/* Engine glow - blue Raptor exhaust */}
          <mesh position={[0, 0, -0.12]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.06, 0.25, 8]} />
            <meshBasicMaterial
              color={0x44aaff}
              transparent
              opacity={0.8}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
        </group>
      ))}

      {/* Engine light */}
      <pointLight
        ref={engineGlowRef}
        position={[0, 0, -1.2]}
        color={0x44aaff}
        intensity={1}
        distance={3}
      />

      {/* Grid fins (near engine section) */}
      {[0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2].map((angle, i) => (
        <mesh key={`grid-${i}`} position={[Math.cos(angle) * 0.3, Math.sin(angle) * 0.3, -0.7]}>
          <boxGeometry args={[0.02, 0.12, 0.1]} />
          <meshStandardMaterial color={0x222222} metalness={0.7} roughness={0.3} />
        </mesh>
      ))}

      {/* Navigation lights - SpaceX style */}
      <mesh position={[0.27, 0, 0.5]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshBasicMaterial color={0xff0000} toneMapped={false} />
      </mesh>
      <mesh position={[-0.27, 0, 0.5]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshBasicMaterial color={0x00ff00} toneMapped={false} />
      </mesh>
    </group>
  );
}
